(ns app.front.view.group.admin.standard-texts.model-codes-download
  (:require [app.domain.model-codes.model :as model-codes-model]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.checkbox-tree :as ui-ct]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ group-id]]
    {:db       (assoc db ::data {::books-loading? true})
     :dispatch [::fetch-available-books group-id]}))

(rf/reg-event-fx ::fetch-available-books
  (fn [_ [_ group-id]]
    {:comm/command {:command    :model-codes/get-available-books
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::fetch-available-books.success group-id]}}))

(def items->db (partial p/index-by :id))

(rf/reg-event-fx ::fetch-available-books.success
  (fn [{:keys [db]} [_ group-id _ {books :items :keys [skipped-items]}]]
    {:db (-> (assoc-in db [::data ::books] books)
             (p/dissoc-in [::data ::books-loading?])
             (assoc-in [::data ::item-db] (items->db books))
             (assoc-in [::data ::browsing-skipped-items] (vec skipped-items)))
     :fx (->> books
              (map (fn [{:keys [id] :as book}]
                     [:dispatch [::fetch-item-children group-id id book]])))}))

(def type-with-children? model-codes-model/item-type-with-children?)

(defn- item-path [item-db item]
  (->> item
       (iterate #(get item-db (:parent-id %)))
       (take-while some?)
       (map model-codes-model/api-item-label-with-ordinal)
       reverse
       vec))

(rf/reg-event-fx ::fetch-item-children
  (fn [{{{::keys [item-db]} ::data} :db}
       [_ group-id book-id {:keys [id type children-fetched?] :as parent-item}]]
    (when (and (not children-fetched?)
               (type-with-children? type))
      {:comm/command {:command    :model-codes/get-item-children
                      :method     :get
                      :data       {:group-id    group-id
                                   :book-id     book-id
                                   :parent-id   id
                                   :parent-type type
                                   :parent-path (item-path item-db parent-item)}
                      :on-success [::fetch-item-children.success parent-item]}})))

(defn- item-children [item-db parent]
  (->> (:child-ids parent)
       (map #(get item-db %))))

(defn- item-selectable?
  "Code item is selectable if it has :content (HTML) or if it has children or if its children are loading"
  [item-db {:keys [content children-fetched? type] :as item}]
  (or content
      (and (type-with-children? type)
           (not children-fetched?))
      (->> (item-children item-db item)
           (some (partial item-selectable? item-db)))))

(defn- updated-item-ancestors [item-db {:keys [id parent-id selected? partially-selected?]}]
  (when-let [parent-item (get item-db parent-id)]
    (let [siblings               (item-children item-db parent-item)
          selectable-sibling-ids (->> siblings
                                      (filter #(item-selectable? item-db %))
                                      (map :id)
                                      set)
          selected-sibling-ids   (-> (->> (filter :selected? siblings)
                                          (map :id)
                                          set)
                                     ((if selected? conj disj) id))
          something-selected?    (not (empty? selected-sibling-ids))
          parent-item            (assoc parent-item
                                        :selected? (and something-selected?
                                                        (= selectable-sibling-ids selected-sibling-ids))
                                        :partially-selected? (or partially-selected?
                                                                 something-selected?))]
      (concat [parent-item]
              (updated-item-ancestors item-db parent-item)))))

(defn- updated-item-descendants [item-db {:keys [selected?] :as item}]
  (->> (item-children item-db item)
       (filter #(item-selectable? item-db %))
       (mapcat (fn [{:keys [type] :as descendant}]
                 (let [updated-child (assoc descendant
                                            :selected? selected?
                                            :partially-selected? (boolean (and selected?
                                                                               (type-with-children? type))))]
                   (concat [updated-child]
                           (updated-item-descendants item-db updated-child)))))))

(rf/reg-event-db ::fetch-item-children.success
  (fn [{{::keys [item-db]} ::data :as db}
       [_ {:keys [id selected?] :as parent-item} _ {:keys [items skipped-items]}]]
    (let [children       (->> items
                              (map (fn [{:keys [type] :as child}]
                                     (assoc child
                                            :parent-id id
                                            :selected? selected?
                                            :children-fetched? (not (type-with-children? type))))))
          updated-parent (merge parent-item
                                {:children-fetched? true
                                 :child-ids         (map :id children)})
          new-item-db    (->> (concat [updated-parent]
                                      children)
                              items->db
                              (merge item-db))
          ;; checking an item will select its children pre-emptively, so if we find out here that the item is not
          ;; in fact selectable, we remove the tick
          new-item-db    (if (not (item-selectable? new-item-db updated-parent))
                           (let [updated-parent (merge updated-parent {:selected?           false
                                                                       :partially-selected? false})]
                             (->> (concat [updated-parent]
                                          (updated-item-ancestors new-item-db updated-parent))
                                  items->db
                                  (merge new-item-db)))
                           new-item-db)]
      (-> db
          (assoc-in [::data ::item-db] new-item-db)
          (update-in [::data ::browsing-skipped-items]
                     #(into [] (distinct) (concat % skipped-items)))))))

(rf/reg-event-db ::select-item
  (fn [{{::keys [item-db]} ::data :as db} [_ item checked?]]
    (if item
      (let [updated-item (assoc item
                                :selected? checked?
                                :partially-selected? (boolean (and checked?
                                                                   (type-with-children? (:type item)))))
            ancestors    (updated-item-ancestors item-db updated-item)
            descendants  (updated-item-descendants item-db updated-item)
            updated-db   (-> (concat ancestors
                                     [updated-item]
                                     descendants)
                             items->db)]
        (update-in db [::data ::item-db] merge updated-db))
      db)))

(rf/reg-event-fx ::download-selected-items
  (fn [{{{::keys [item-db]} ::data} :db} [_ group-id]]
    (let [all-items (vals item-db)
          id-set    (fn [k]
                      (->> all-items
                           (filter k)
                           (map :id)
                           set))]
      {:dispatch [:comm/command-monitored {:command    :model-codes/save-group-selections
                                           :method     :post
                                           :data       {:group-id         group-id
                                                        :selected-items   (id-set :selected?)
                                                        :selected-parents (id-set :partially-selected?)}
                                           :on-success [::download-selected-items.success]}]})))

(rf/reg-event-fx ::download-selected-items.success
  (fn [{:keys [db]} [_ status {:keys [changes skipped-items]} request-id]]
    {:db         (assoc-in db [::data ::import-skipped-items] (vec skipped-items))
     :dispatch-n [[::entities/apply-changes status changes]
                  (when (empty? skipped-items)
                    [:notifications/add :toast :success :success
                     :model-codes/download-success request-id])]}))

(rf/reg-sub ::download-in-progress?
  :<- [:comm/command-processing? :model-codes/save-group-selections]
  :-> identity)

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::browsing-skipped-items
  :<- [::data]
  :-> ::browsing-skipped-items)

(rf/reg-sub ::import-skipped-items
  :<- [::data]
  :-> ::import-skipped-items)

(rf/reg-sub ::books-loading?
  :<- [::data]
  :-> ::books-loading?)

(rf/reg-sub ::available-books
  :<- [::data]
  (fn [{::keys [books item-db]} _]
    (->> books
         (map #(get item-db (:id %))))))

(rf/reg-sub ::item-db
  :<- [::data]
  :-> ::item-db)

(rf/reg-sub ::item-children
  :<- [::item-db]
  (fn [item-db [_ parent]]
    (item-children item-db parent)))

(rf/reg-sub ::item-children-loading?
  (fn [_ [_ parent]]
    (not (:children-fetched? parent))))

(rf/reg-sub ::item-selectable?
  :<- [::item-db]
  (fn [item-db [_ item]]
    (item-selectable? item-db item)))

(rf/reg-sub ::download-available?
  :<- [::item-db]
  :<- [::download-in-progress?]
  (fn [[item-db in-progress?] _]
    (and (not in-progress?)
         (->> item-db
              vals
              (some :selected?)))))

(defn content-rd-renderer [{:keys [id content]}]
  [ui-rt/editor-legacy {:id                 (str id "-content")
                        :initial-html-value content}])

(defn- skipped-items-warning [tr guide-id title-key message-key skipped-items]
  (when (seq skipped-items)
    [:div {:data-guide-id    guide-id
           :data-guide-label (tr [title-key])
           :role             "alert"}
     [ui/help
      [:div
       [:p.font-bold (tr [title-key])]
       [:p (tr [message-key])]
       [:ul.list-disc.pl-5
        (for-frag [{:keys [title path]} skipped-items]
          [:li (if (seq path)
                 (tr [:model-codes/skipped-item-in-path] [title (str/join " / " path)])
                 title)])]]]]))

(defn section []
  (let [tr                     (<sub [:tr])
        group-id               (<sub [::gas/group-id])
        books                  (<sub [::available-books])
        dl-available?          (<sub [::download-available?])
        loading?               (<sub [::books-loading?])
        downloading?           (<sub [::download-in-progress?])
        browsing-skipped-items (<sub [::browsing-skipped-items])
        import-skipped-items   (<sub [::import-skipped-items])]
    [acommon/section {}
     [:div.flex.flex-col.gap-4
      [skipped-items-warning tr "model-codes-browsing-skips"
       :model-codes/skipped-before-download-title
       :model-codes/skipped-before-download
       browsing-skipped-items]
      [skipped-items-warning tr "model-codes-import-skips"
       :model-codes/skipped-after-download-title
       :model-codes/skipped-after-download
       import-skipped-items]
      (cond
        loading?
        (cc/loading-spinner {:with-overlay false})

        (empty? books)
        [:div (tr [:model-codes/no-books])]

        :else
        [:<>
         [ui-ct/checkbox-tree (util/mmap {:items                     books
                                          :expand-evt                [::fetch-item-children group-id]
                                          :select-evt                [::select-item]
                                          :children-sub              [::item-children]
                                          :children-loading-sub      [::item-children-loading?]
                                          :selectable-sub            [::item-selectable?]
                                          :render-content            content-rd-renderer
                                          :item->label               model-codes-model/api-item-label-with-ordinal
                                          :item->selected?           :selected?
                                          :item->partially-selected? :partially-selected?}
                                         tr)]
         [ui-action-buttons/action-buttons
          {:align    :left
           :loading? (boolean downloading?)
           :buttons  [{:on-click  #(>evt [::download-selected-items group-id])
                       :text      (tr [:model-codes/download])
                       :disabled? (not dl-available?)}]}]])]]))
